"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ListCardBody = ListCardBody;
exports.ListCards = ListCards;
exports.ListViewToggle = ListViewToggle;
exports.CardSettings = CardSettings;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const shared_1 = require("@forecastify/shared");
const cn_1 = require("../lib/cn");
const button_1 = require("./button");
const cell_view_1 = require("./cell-view");
const dialog_1 = require("./dialog");
const icon_1 = require("./icon");
const row_controls_1 = require("./row-controls");
const select_1 = require("./select");
const skeleton_1 = require("./skeleton");
const toggle_1 = require("./toggle");
const tooltip_1 = require("./tooltip");
/** The card's contents without its frame — what the task board puts inside its own card. */
function ListCardBody({ row, card, labelOf, corner }) {
    const cell = (key) => key === null ? null : (0, jsx_runtime_1.jsx)(cell_view_1.CellView, { value: row.cells[key] });
    const details = card.details.filter((key) => key !== null);
    /**
     * ⚠️ 21 Oct 2026 · **the context line carries no colour dot.** A client's swatch beside its name
     * was drawn on some lists and not others, and at the card's size it read as a status. The table
     * keeps it; the card is words.
     */
    const contextCell = () => {
        if (card.context === null)
            return null;
        const value = row.cells[card.context];
        if (value === undefined || value === null || typeof value !== 'object') {
            return (0, jsx_runtime_1.jsx)(cell_view_1.CellView, { value: value });
        }
        const { dot: _dot, ring: _ring, fill: _fill, ...words } = value;
        return (0, jsx_runtime_1.jsx)(cell_view_1.CellView, { value: words });
    };
    /**
     * 26 Oct 2026 · **the badge is one line, its `sub` stays in the table.** The header row holds the
     * context, the badge and the corner side by side; a second line under the badge (a task's
     * *"Jira: Backlog – nincs leképezve"*) crowded it into the title. The table keeps it under the
     * status, and the record's own page explains it.
     */
    const badgeCell = () => {
        if (card.badge === null)
            return null;
        const value = row.cells[card.badge];
        if (value === undefined || value === null || typeof value !== 'object') {
            return (0, jsx_runtime_1.jsx)(cell_view_1.CellView, { value: value });
        }
        const { sub: _sub, ...line } = value;
        return (0, jsx_runtime_1.jsx)(cell_view_1.CellView, { value: line });
    };
    /**
     * ⚠️⚠️ 21 Oct 2026 · **THE PERSON SITS UNDER THE TITLE, FACE AND NAME, ON EVERY CARD.** It was
     * the bottom-right corner, which pushed a lone name into a row of its own and broke the card's
     * rhythm; and a list that passed only a name drew a bare word where the next drew a face. A
     * person cell with no face of its own gets its initials, so every card draws one.
     */
    const personCell = () => {
        if (card.avatar === null)
            return null;
        const raw = row.cells[card.avatar];
        if (raw === undefined || raw === null)
            return null;
        const value = typeof raw === 'object' ? raw : { text: raw };
        const name = value.text === undefined ? '' : String(value.text);
        const withFace = value.avatar !== undefined || name === '' || name === '–' || value.muted === true
            ? value
            : { ...value, avatar: { initials: initialsOf(name), title: name } };
        return (0, jsx_runtime_1.jsx)(cell_view_1.CellView, { value: withFace });
    };
    return ((0, jsx_runtime_1.jsxs)("div", { className: "flex min-w-0 flex-col gap-2", children: [card.context === null && card.badge === null && corner === undefined ? null : ((0, jsx_runtime_1.jsxs)("div", { className: "flex min-w-0 items-center gap-2", children: [(0, jsx_runtime_1.jsx)("span", { className: "text-muted-foreground min-w-0 flex-1 truncate text-[11.5px]", children: contextCell() }), card.badge === null ? null : (0, jsx_runtime_1.jsx)("span", { className: "shrink-0", children: badgeCell() }), corner] })), (0, jsx_runtime_1.jsx)("div", { className: "line-clamp-2 min-w-0 text-[13.5px] leading-snug font-medium break-words", children: cell(card.title) }), card.avatar === null ? null : ((0, jsx_runtime_1.jsx)("div", { className: "text-muted-foreground flex min-w-0 items-center text-[12px]", children: personCell() })), details.length === 0 ? null : ((0, jsx_runtime_1.jsx)("dl", { className: "grid min-w-0 grid-cols-2 gap-x-3 gap-y-1.5", children: details.map((key) => ((0, jsx_runtime_1.jsxs)("div", { className: "min-w-0", children: [(0, jsx_runtime_1.jsx)("dt", { className: "text-muted-foreground truncate text-[10.5px] font-medium tracking-wide uppercase", children: labelOf(key) }), (0, jsx_runtime_1.jsx)("dd", { className: "flex min-w-0 items-center text-[12.5px]", children: cell(key) })] }, key))) })), card.footer === null ? null : ((0, jsx_runtime_1.jsx)("div", { className: "text-muted-foreground flex min-w-0 items-center text-[12px]", children: cell(card.footer) }))] }));
}
function initialsOf(name) {
    return name
        .split(/\s+/u)
        .filter((part) => part !== '')
        .slice(0, 2)
        .map((part) => part[0]?.toUpperCase() ?? '')
        .join('');
}
/** How long a touch must rest on a card before it selects rather than opens. */
const LONG_PRESS_MS = 450;
/**
 * The grid of cards: one column on a phone, as many ~280px columns as fit on a desktop.
 *
 * ⚠️ **Selecting**: the checkbox shows on hover and focus, and stays once anything is selected; on a
 * phone, where there is no hover, a long press selects (the board's touch rule: `board.tsx`). A
 * press that selected does not also open the card.
 */
function ListCards({ rows, card, labelOf, isLoading, onOpen, rowMenu, rowMenuLabel, selectable, selected, onToggle, selectLabel, }) {
    const press = (0, react_1.useRef)(null);
    const selecting = selected.length > 0;
    if (isLoading) {
        return ((0, jsx_runtime_1.jsx)("div", { className: "grid grid-cols-1 gap-3 p-3 sm:grid-cols-[repeat(auto-fill,minmax(280px,1fr))]", children: Array.from({ length: 6 }, (_, at) => ((0, jsx_runtime_1.jsxs)("div", { "aria-hidden": true, className: "bg-card flex flex-col gap-2 rounded-[10px] border p-3", children: [(0, jsx_runtime_1.jsx)(skeleton_1.Skeleton, { width: 90 }), (0, jsx_runtime_1.jsx)(skeleton_1.Skeleton, { width: 180 }), (0, jsx_runtime_1.jsx)(skeleton_1.Skeleton, { width: 140 })] }, at))) }));
    }
    return ((0, jsx_runtime_1.jsx)("ul", { className: "grid grid-cols-1 gap-3 p-3 sm:grid-cols-[repeat(auto-fill,minmax(280px,1fr))]", children: rows.map((row) => {
            const on = selected.includes(row.id);
            return ((0, jsx_runtime_1.jsx)("li", { "data-state": on ? 'selected' : undefined, className: (0, cn_1.cn)('group bg-card relative min-w-0 rounded-[10px] border p-3 transition-colors', on ? 'border-primary/50 bg-primary/5' : 'hover:bg-muted/40', 
                /* 30 Oct 2026 · a group's member, on a tint — the grid has no room to indent. */
                row.nested === true && !on && 'bg-muted/30', onOpen !== undefined && 'cursor-pointer'), onClick: (event) => {
                    if (event.target.closest('[data-stop]') !== null)
                        return;
                    if (press.current?.fired === true) {
                        press.current = null;
                        return;
                    }
                    if (selecting && selectable) {
                        onToggle(row.id);
                        return;
                    }
                    onOpen?.(row.id);
                }, onPointerDown: (event) => {
                    if (!selectable || event.pointerType !== 'touch')
                        return;
                    const timer = window.setTimeout(() => {
                        if (press.current !== null)
                            press.current.fired = true;
                        onToggle(row.id);
                    }, LONG_PRESS_MS);
                    press.current = { timer, fired: false };
                }, onPointerUp: () => {
                    if (press.current !== null && !press.current.fired) {
                        window.clearTimeout(press.current.timer);
                        press.current = null;
                    }
                }, onPointerLeave: () => {
                    if (press.current !== null && !press.current.fired) {
                        window.clearTimeout(press.current.timer);
                        press.current = null;
                    }
                }, children: (0, jsx_runtime_1.jsx)(ListCardBody, { row: row, card: card, labelOf: labelOf, corner: (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [!selectable ? null : ((0, jsx_runtime_1.jsx)("span", { "data-stop": "", className: (0, cn_1.cn)('shrink-0 transition-opacity', selecting || on
                                    ? 'opacity-100'
                                    : 'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100'), children: (0, jsx_runtime_1.jsx)(toggle_1.Checkbox, { checked: on, label: selectLabel, onCheckedChange: () => {
                                        onToggle(row.id);
                                    } }) })), row.group === undefined ? null : (0, jsx_runtime_1.jsx)(row_controls_1.GroupToggle, { group: row.group }), rowMenu === undefined ? null : ((0, jsx_runtime_1.jsx)("span", { "data-stop": "", className: "-my-1 -mr-1 shrink-0", children: (0, jsx_runtime_1.jsx)(row_controls_1.RowMenu, { items: rowMenu(row.id), label: rowMenuLabel }) }))] }) }) }, row.id));
        }) }));
}
/** Table or cards — two icons in the toolbar. */
function ListViewToggle({ value, onChange, labels, }) {
    return ((0, jsx_runtime_1.jsx)("div", { role: "group", "aria-label": labels.view, className: "bg-muted flex shrink-0 items-center gap-0.5 rounded-md p-0.5", children: [
            ['list', 'list', labels.list],
            ['cards', 'layout-grid', labels.cards],
        ].map(([option, icon, label]) => ((0, jsx_runtime_1.jsx)(tooltip_1.Tooltip, { asChild: true, content: label, children: (0, jsx_runtime_1.jsx)("button", { type: "button", "aria-pressed": value === option, "aria-label": label, 
                /* ⚠️ `h-7` inside the 2px track — 32px in all, the small button's height beside it. */
                className: (0, cn_1.cn)('grid h-7 w-8 place-items-center rounded transition-colors', value === option
                    ? 'bg-background text-foreground shadow-xs'
                    : 'text-muted-foreground hover:text-foreground'), onClick: () => {
                    onChange(option);
                }, children: (0, jsx_runtime_1.jsx)(icon_1.Icon, { name: icon, size: 14 }) }) }, option))) }));
}
/**
 * ┌───────────────────────────────────────────────────────────────────────┐
 * │ What the card shows — a picker per free slot, and the card itself as  │
 * │ the preview.                                                          │
 * └───────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **The title and context are shown and not offered**, so the person sees what does not move
 * rather than wondering why it is missing from the list. Any column can go in any free slot,
 * metafields included; a column already in one slot is still offered in another, because showing
 * hours twice is a choice, not an error.
 */
function CardSettings({ open, onOpenChange, columns, card, sample, labels, onSave, onReset, }) {
    const labelOf = (key) => columns.find((column) => column.key === key)?.label ?? key;
    const options = [
        { value: '', label: labels.none },
        ...columns
            .filter((column) => column.key !== card.title && column.key !== card.context)
            .map((column) => ({ value: column.key, label: column.label })),
    ];
    const [draft, setDraft] = (0, react_1.useState)(() => ({
        badge: card.badge,
        avatar: card.avatar,
        details: [...card.details],
        footer: card.footer,
    }));
    const set = (patch) => {
        const next = { ...draft, ...patch };
        setDraft(next);
        onSave(next);
    };
    const preview = { ...card, ...draft };
    const slot = (label, value, onPick) => ((0, jsx_runtime_1.jsxs)("label", { className: "flex flex-col gap-1", children: [(0, jsx_runtime_1.jsx)("span", { className: "text-muted-foreground text-[11px] font-medium tracking-wide uppercase", children: label }), (0, jsx_runtime_1.jsx)(select_1.Select, { value: value ?? '', options: options, searchable: options.length > 8, onChange: (event) => {
                    onPick(event.target.value === '' ? null : event.target.value);
                } })] }));
    return ((0, jsx_runtime_1.jsx)(dialog_1.Dialog, { open: open, onOpenChange: onOpenChange, title: labels.settings, closeLabel: labels.close, width: 560, footer: (0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)(button_1.Button, { variant: "ghost", onClick: () => {
                        onReset();
                        onOpenChange(false);
                    }, children: labels.reset }), (0, jsx_runtime_1.jsx)("div", { className: "flex-1" }), (0, jsx_runtime_1.jsx)(button_1.Button, { onClick: () => {
                        onOpenChange(false);
                    }, children: labels.done })] }), children: (0, jsx_runtime_1.jsxs)("div", { className: "flex flex-col gap-4", children: [sample === null ? null : ((0, jsx_runtime_1.jsxs)("div", { className: "flex flex-col gap-1", children: [(0, jsx_runtime_1.jsx)("span", { className: "text-muted-foreground text-[11px] font-medium tracking-wide uppercase", children: labels.preview }), (0, jsx_runtime_1.jsx)("div", { className: "bg-card rounded-[10px] border p-3", children: (0, jsx_runtime_1.jsx)(ListCardBody, { row: sample, card: preview, labelOf: labelOf }) })] })), (0, jsx_runtime_1.jsx)("p", { className: "text-muted-foreground text-[12px]", children: `${labels.title}: ${labelOf(card.title)}${card.context === null ? '' : ` · ${labels.context}: ${labelOf(card.context)}`}` }), (0, jsx_runtime_1.jsxs)("div", { className: "grid grid-cols-1 gap-3 sm:grid-cols-2", children: [slot(labels.badge, draft.badge, (value) => {
                            set({ badge: value });
                        }), slot(labels.avatar, draft.avatar, (value) => {
                            set({ avatar: value });
                        }), Array.from({ length: shared_1.CARD_DETAIL_SLOTS }, (_, at) => slot(labels.detail(at + 1), draft.details[at] ?? null, (value) => {
                            const details = Array.from({ length: shared_1.CARD_DETAIL_SLOTS }, (__, index) => draft.details[index] ?? null);
                            details[at] = value;
                            set({ details });
                        })).map((node, at) => ((0, jsx_runtime_1.jsx)("div", { children: node }, `detail-${String(at)}`))), slot(labels.footer, draft.footer, (value) => {
                            set({ footer: value });
                        })] })] }) }));
}
//# sourceMappingURL=list-card.js.map